import React, { useState } from 'react'
import { SignableCreditApplicationDocumentMeta, UnderwritingOffer } from '_gql'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import { TOAST_TYPE_DANGER, TOAST_TYPE_SUCCESS, useToast } from '@divvy-web/skylab.toast'

import AgreementTakeover from '@sputnik/components/AgreementTakeover'
import useGetIsLoggedInAsAuthSigner from '@sputnik/hooks/useGetIsLoggedInAsAuthSigner'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'

import { useAcceptTilaAgreement } from './AcceptTilaAgreement.gql'
import AgreementLink from './AgreementLink'
import ApplicationTerms from './ApplicationTerms'
import { GetCreditApplicationTilaInfo_creditApplication_CreditApplication as CreditApplicationType } from './GetCreditApplicationTilaInfo.gql'
import styles from './styles.module.scss'

export interface ApplicationAgreementsProps {
  creditApplication: CreditApplicationType
  hasAgreedToTerms: boolean
  offerType?: UnderwritingOffer | null
  decisionId?: SignableCreditApplicationDocumentMeta['underwritingDecisionUuid']
  hasAgreedToTila: boolean
  onCheckboxClick: (e: React.ChangeEvent) => void
}

const ApplicationAgreements = ({
  creditApplication,
  hasAgreedToTerms,
  hasAgreedToTila,
  offerType,
  decisionId,
  onCheckboxClick,
}: ApplicationAgreementsProps) => {
  const showSuccessToast = useToast(TOAST_TYPE_SUCCESS) as any
  const showDangerToast = useToast(TOAST_TYPE_DANGER) as any
  const [getClassName, makeTestId] = useNameFormatter('ApplicationAgreements')
  const [isShowingAgreementTakeover, setIsShowingAgreementTakeover] = useState(false)
  const { tila, salesforceCreditId } = creditApplication || {}
  const needsTilaAgreement = !!tila
  const tilaContent = tila?.content
  const disableApplicationTerms = needsTilaAgreement && !hasAgreedToTila

  const { isLoggedInUserAuthorizedSigner } = useGetIsLoggedInAsAuthSigner({
    authSignerEmail: creditApplication?.authorizedSigner?.email || '',
  })

  const [acceptTilaAgreement, { loading: isAcceptingTilaAgreement }] = useAcceptTilaAgreement({
    onCompleted: () => {
      setIsShowingAgreementTakeover(false)

      logInfo('acceptTilaAgreement', {
        attributes: {
          creditAppId: salesforceCreditId,
          result: 'accepts agreement terms for application',
        },
        eventName: 'applicationAgreements',
      })

      showSuccessToast(
        <FormattedMessage
          defaultMessage='You have successfully agreed to your offer summary'
          id='sputnik.ApplicationAgreements__VFXi23'
        />,
      )
    },
    onError: (e) => {
      setIsShowingAgreementTakeover(false)

      logError(e, {
        attributes: {
          creditAppId: salesforceCreditId,
          action: 'acceptTilaAgreement',
          message: e?.message,
          result: 'Error accepting agreement terms for application',
        },
        eventName: 'applicationAgreements',
      })

      showDangerToast(
        <FormattedMessage
          defaultMessage='There was an error agreeing to your offer summary.'
          id='sputnik.ApplicationAgreements__9fJ3od'
        />,
        { className: 'dd-privacy-allow' },
      )
    },
  })

  const handleAgreeToOfferSummary = () => {
    if (offerType) {
      acceptTilaAgreement({
        variables: {
          salesforceCreditId: salesforceCreditId as string,
          underwritingDecisionUuid: decisionId,
          underwritingOfferType: offerType,
        },
        refetchQueries: ['GetTilaInfo'],
      })
    }
  }

  return (
    <div
      className={`${styles[getClassName('')]} dd-privacy-allow`}
      data-testid={makeTestId('')}
    >
      {needsTilaAgreement && (
        <AgreementLink
          hasAgreedToTila={hasAgreedToTila}
          onClick={() => setIsShowingAgreementTakeover(true)}
        />
      )}
      <ApplicationTerms
        creditApplication={creditApplication}
        disableApplicationTerms={disableApplicationTerms}
        hasAgreedToTerms={hasAgreedToTerms}
        onCheckboxClick={onCheckboxClick}
      />
      {tilaContent && offerType && (
        <AgreementTakeover
          hasAgreedToTila={hasAgreedToTila}
          isAcceptingTilaAgreement={isAcceptingTilaAgreement}
          isLoggedInUserAuthorizedSigner={isLoggedInUserAuthorizedSigner}
          isShowing={isShowingAgreementTakeover}
          tilaContent={tilaContent}
          onAgreeClick={handleAgreeToOfferSummary}
          onCancel={() => setIsShowingAgreementTakeover(false)}
        />
      )}
    </div>
  )
}

export default ApplicationAgreements
